import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';

export interface UploadStatus {
    status: 'compressing' | 'uploading' | 'success' | 'error';
    errorMessage?: string;
    fileExtension: string;
    remoteName?: string;
    sasDownloadLink?: string;
    localId?: string;
}

type Image = {
    id: string;
    url: string | null;
    fileExtension: string | undefined;
};

interface SingleImageUploaderProps {
    confirmedImage: Image | undefined;
    setConfirmedImage: React.Dispatch<React.SetStateAction<Image | undefined>>;
    upload?: UploadStatus;
    onUpload: (file: File, localId: string) => void;
    onRemoveUpload?: (localId: string) => void;
}

export function SingleImageUploader({
                                                confirmedImage,
                                                setConfirmedImage,
                                                upload,
                                                onUpload,
                                                onRemoveUpload,
                                            }: SingleImageUploaderProps) {
    const [fileMap, setFileMap] = useState<Record<string, File>>({});
    const previewUrlsRef = useRef<Record<string, string>>({});

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (confirmedImage) return;
        const file = acceptedFiles[0];
        if (!file) return;

        const localId = crypto.randomUUID();
        setFileMap((prev) => ({ ...prev, [localId]: file }));
        onUpload(file, localId);
    }, [confirmedImage, onUpload]);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/webp': [],
        },
        multiple: false,
        noClick: !!confirmedImage,
        noDrag: !!confirmedImage,
    });

    const currentImageSrc =
        confirmedImage?.url ||
        (upload?.localId && previewUrlsRef.current[upload.localId]) ||
        undefined;

    const retryUpload = (localId: string) => {
        const file = fileMap[localId];
        if (!file) return;
        onUpload(file, localId);
    };

    useEffect(() => {
        if (
            upload?.status === 'success' &&
            upload.remoteName &&
            (upload.sasDownloadLink || previewUrlsRef.current[upload.localId!])
        ) {
            setConfirmedImage({
                id: upload.remoteName,
                url: previewUrlsRef.current[upload.localId!],
                fileExtension: upload.fileExtension ?? undefined,
            });
            if (upload.localId) onRemoveUpload?.(upload.localId);
        }
    }, [upload, setConfirmedImage, onRemoveUpload]);

    useEffect(() => {
        const urlsToCleanUp = { ...previewUrlsRef.current };
        return () => {
            for (const url of Object.values(urlsToCleanUp)) {
                URL.revokeObjectURL(url);
            }
        };
    }, []);

    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    border: '1px dashed gray',
                    borderRadius: '5px',
                    padding: 2,
                    textAlign: 'center',
                    backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
                    cursor: confirmedImage ? 'not-allowed' : 'pointer',
                }}
            >
                <input {...getInputProps()} />
                <Typography variant="body2">
                    {confirmedImage
                        ? 'Bild bereits hochgeladen, entferne das bestehende zuerst'
                        : 'Ziehe ein Bild hierher oder klicke zum Hinzufügen'}
                </Typography>
            </Box>

            {/* Confirmed image */}
            {confirmedImage && (
                <Box mt={2} sx={{
                    width: 150,
                    height: 150,
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 1,
                }}>
                    <img
                        src={currentImageSrc}
                        alt="Uploaded"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                        onClick={() => setConfirmedImage(undefined)}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            '&:hover': { backgroundColor: 'white' },
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {/* Upload in progress */}
            {upload && !confirmedImage && upload.localId && fileMap[upload.localId] && (() => {
                const file = fileMap[upload.localId!];
                const id = upload.localId!;
                if (!previewUrlsRef.current[id]) {
                    previewUrlsRef.current[id] = URL.createObjectURL(file);
                }
                const previewUrl = previewUrlsRef.current[id];

                return (
                    <Box key={id} mt={2} sx={{
                        width: 150,
                        height: 150,
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: upload.status === 'error' ? '#ffeaea' : '#f5f5f5',
                    }}>
                        <img
                            src={previewUrl}
                            alt="Preview"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: upload.status === 'error' ? 0.3 : 0.5,
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                bottom: 2,
                                left: 0,
                                right: 0,
                                textAlign: 'center',
                                backgroundColor: 'rgba(255,255,255,0.7)',
                            }}
                            color={upload.status === 'error' ? 'error' : undefined}
                        >
                            {upload.status === 'compressing'
                                ? 'Komprimieren'
                                : upload.status === 'uploading'
                                    ? 'Hochladen'
                                    : upload.status === 'error'
                                        ? 'Fehler'
                                        : ''}
                        </Typography>

                        {upload.status === 'compressing' || upload.status === 'uploading' ? (
                            <CircularProgress size={24} sx={{ position: 'absolute' }} />
                        ) : null}

                        {upload.status === 'error' && (
                            <>
                                <IconButton
                                    onClick={() => retryUpload(id)}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        left: 2,
                                        backgroundColor: 'rgba(255,255,255,0.7)',
                                        '&:hover': { backgroundColor: 'white' },
                                    }}
                                >
                                    <ReplayIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                    onClick={() => onRemoveUpload?.(id)}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        backgroundColor: 'rgba(255,255,255,0.7)',
                                        '&:hover': { backgroundColor: 'white' },
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </>
                        )}
                    </Box>
                );
            })()}
        </Box>
    );
}